import React, { useEffect, useMemo, useState } from 'react';
import PageContainer from 'src/components/container/PageContainer';
import { useMain } from '../hooks/useMain';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';
import {
  Box,
  Grid,
  Card,
  Typography,
  TextField,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Tooltip,
  Stack,
  Avatar,
  Divider,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import CoffeeOutlinedIcon from '@mui/icons-material/CoffeeOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendIcon from '@mui/icons-material/Send';

// ─── EXACT DASHBOARD PALETTE ─────────────────────────────────────────────
// Pulled directly from the "YourSalon" dashboard overview cards & sidebar
const THEME = {
  purple: '#6C63E0', // sidebar / brand
  purpleDark: '#5347C7',
  orange: '#F0501E', // Today Sales card
  orangeBg: '#FFEDE7',
  teal: '#1FC6A6', // Today Appointments card
  tealBg: '#E3FBF6',
  blue: '#3D6DF2', // Today Expenses card
  blueBg: '#E9EEFE',
  yellow: '#F5B301', // Today Clients Visit card
  yellowBg: '#FFF6DF',
  border: '#EDEDF5',
  textSecondary: '#8A8FA3',
  ink: '#1A1A2E',
};

// const LEAVE_TYPES = [
//   { type: 'Casual', icon: <CoffeeOutlinedIcon fontSize="small" />, color: THEME.blue, bg: THEME.blueBg },
//   { type: 'Sick', icon: <LocalHospitalOutlinedIcon fontSize="small" />, color: THEME.orange, bg: THEME.orangeBg },
//   { type: 'Paid', icon: <StarBorderOutlinedIcon fontSize="small" />, color: THEME.teal, bg: THEME.tealBg },
//   { type: 'Unpaid', icon: <DescriptionOutlinedIcon fontSize="small" />, color: THEME.purple, bg: '#EFEDFB' },
// ];

const STATUS_META = {
  Approved: { color: THEME.teal, bg: THEME.tealBg, icon: <CheckCircleOutlineIcon sx={{ fontSize: 15 }} /> },
  Rejected: { color: THEME.orange, bg: THEME.orangeBg, icon: <CancelOutlinedIcon sx={{ fontSize: 15 }} /> },
  Pending: { color: THEME.yellow, bg: THEME.yellowBg, icon: <AccessTimeIcon sx={{ fontSize: 15 }} /> },
};

const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const formatDisplay = (dateStr) => {
  if (!dateStr) return '--';
  const [y, m, d] = dateStr.split('T')[0].split('-');
  if (!y || !m || !d) return dateStr;
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Top stat card, styled exactly like the dashboard overview tiles ────────
const StatCard = ({ icon, value, label, bg, iconColor }) => (
  <Card
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: '16px',
      bgcolor: bg,
      color: '#fff',
      position: 'relative',
      minHeight: 118,
      overflow: 'hidden',
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.9)', color: iconColor, width: 40, height: 40 }}>
        {icon}
      </Avatar>
      <ArrowForwardIcon sx={{ opacity: 0.85 }} />
    </Stack>
    <Typography variant="h4" fontWeight={800} sx={{ mt: 2 }}>
      {value}
    </Typography>
    <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, opacity: 0.9 }}>
      {label}
    </Typography>
  </Card>
);

const Leave = () => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const { activeStaffId, branchId, fetchStaffLeaves, applyStaffLeave } = useMain();

  // ─── Apply form state ───────────────────────────────────
//   const [leaveType, setLeaveType] = useState('Casual');
  const [dayType, setDayType] = useState('Full Day');
  const [fromDate, setFromDate] = useState(todayStr());
  const [toDate, setToDate] = useState(todayStr());
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ─── Log state ──────────────────────────────────────────
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('All');

  const getLeaves = async (id) => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await fetchStaffLeaves(id, branchId ? `?branchId=${branchId}` : '');
      if (res?.statusCode === 200) {
        setLeaves(res?.data?.leaves || []);
      } else {
        setLeaves([]);
      }
    } catch (error) {
      setLeaves([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeStaffId) {
      getLeaves(activeStaffId);
    }
  }, [activeStaffId, branchId]);

  const counts = useMemo(() => {
    return {
      All: leaves.length,
      Pending: leaves.filter((l) => l.status === 'Pending').length,
      Approved: leaves.filter((l) => l.status === 'Approved').length,
      Rejected: leaves.filter((l) => l.status === 'Rejected').length,
    };
  }, [leaves]);

  const filteredLeaves = useMemo(() => {
    const sorted = [...leaves].sort((a, b) => new Date(b.appliedOn) - new Date(a.appliedOn));
    if (filter === 'All') return sorted;
    return sorted.filter((l) => l.status === filter);
  }, [leaves, filter]);

  const calculatedDays = useMemo(() => {
    if (!fromDate || !toDate || fromDate > toDate) return 0;
    const [y1, m1, d1] = fromDate.split('-').map(Number);
    const [y2, m2, d2] = toDate.split('-').map(Number);
    const start = new Date(y1, m1 - 1, d1);
    const end = new Date(y2, m2 - 1, d2);
    const diff = Math.ceil(Math.abs(end - start) / 86400000) + 1;
    return dayType === 'Half Day' ? 0.5 : diff;
  }, [fromDate, toDate, dayType]);

  const handleSubmit = async () => {
    if (!activeStaffId || !fromDate || !toDate || fromDate > toDate) return;

    const payload = {
      fromDate,
      toDate,
    //   leaveType,
      leaveDayType: dayType,
      reason: reason.trim(),
    };

    try {
      setSubmitting(true);
      const res = await applyStaffLeave(activeStaffId, payload);
      if (res?.statusCode === 200 || res?.statusCode === 201) {
        setReason('');
        setFromDate(todayStr());
        setToDate(todayStr());
        getLeaves(activeStaffId);
      }
    } catch (error) {
      // handle error
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer title="Leave" description="Apply for leave and track your leave requests">
      <Box>
        <Typography variant="h4" fontWeight={800} sx={{ color: THEME.ink, mb: 2.5 }}>
          Leave Management
        </Typography>

        {/* ── TOP OVERVIEW CARDS (styled like dashboard tiles) ─────── */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard icon={<EventAvailableIcon />} value={counts.All} label="TOTAL REQUESTS" bg={THEME.orange} iconColor={THEME.orange} />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard icon={<AccessTimeIcon />} value={counts.Pending} label="PENDING" bg={THEME.teal} iconColor={THEME.teal} />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard icon={<CheckCircleOutlineIcon />} value={counts.Approved} label="APPROVED" bg={THEME.blue} iconColor={THEME.blue} />
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <StatCard icon={<SentimentSatisfiedAltIcon />} value={counts.Rejected} label="REJECTED" bg={THEME.yellow} iconColor={THEME.yellow} />
          </Grid>
        </Grid>

        {/* ── SINGLE TAB: LEFT = APPLY, RIGHT = LOG ─────────────────── */}
        <Grid container spacing={3}>
          {/* LEFT: Apply Leave */}
          <Grid item xs={12} md={5}>
            <Card elevation={0} sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: '18px', border: `1px solid ${THEME.border}`, height: '100%' }}>
              <Stack direction="row" alignItems="center" spacing={1.2} mb={2.5}>
                <Avatar sx={{ bgcolor: '#EFEDFB', color: THEME.purple, width: 34, height: 34 }}>
                  <PersonAddAltIcon fontSize="small" />
                </Avatar>
                <Typography variant="h6" fontWeight={800}>
                  Apply for Leave
                </Typography>
              </Stack>

              {/* <Typography variant="caption" fontWeight={700} sx={{ color: THEME.textSecondary }}>
                LEAVE TYPE
              </Typography>
              <Grid container spacing={1.2} sx={{ mt: 0.3, mb: 2.5 }}>
                {LEAVE_TYPES.map((item) => {
                  const selected = leaveType === item.type;
                  return (
                    <Grid item xs={6} key={item.type}>
                      <Box
                        onClick={() => setLeaveType(item.type)}
                        sx={{
                          cursor: 'pointer',
                          borderRadius: '12px',
                          p: 1.3,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          border: `2px solid ${selected ? item.color : THEME.border}`,
                          bgcolor: selected ? item.bg : '#fff',
                          transition: 'all 0.15s ease',
                          '&:hover': { borderColor: item.color },
                        }}
                      >
                        <Avatar sx={{ bgcolor: selected ? item.color : '#F3F4F6', color: selected ? '#fff' : item.color, width: 26, height: 26 }}>
                          {item.icon}
                        </Avatar>
                        <Typography variant="body2" fontWeight={700} sx={{ color: selected ? item.color : THEME.ink }}>
                          {item.type}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid> */}

              <Typography variant="caption" fontWeight={700} sx={{ color: THEME.textSecondary }}>
                DURATION
              </Typography>
              <Stack direction="row" spacing={1.2} sx={{ mt: 0.3, mb: 2.5 }}>
                {['Full Day', 'Half Day'].map((d) => {
                  const selected = dayType === d;
                  return (
                    <Box
                      key={d}
                      onClick={() => setDayType(d)}
                      sx={{
                        flex: 1,
                        cursor: 'pointer',
                        textAlign: 'center',
                        py: 1.1,
                        borderRadius: '10px',
                        fontWeight: 700,
                        fontSize: 13,
                        border: `2px solid ${selected ? THEME.purple : THEME.border}`,
                        bgcolor: selected ? '#EFEDFB' : '#fff',
                        color: selected ? THEME.purple : THEME.ink,
                      }}
                    >
                      {d}
                    </Box>
                  );
                })}
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From"
                  InputLabelProps={{ shrink: true }}
                  value={fromDate}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFromDate(v);
                    if (toDate < v) setToDate(v);
                  }}
                />
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: fromDate }}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </Stack>

              <Chip
                icon={<CalendarMonthIcon sx={{ fontSize: 16, color: `${THEME.purple} !important` }} />}
                label={`${calculatedDays} ${calculatedDays === 1 ? 'Day' : 'Days'} total`}
                sx={{ bgcolor: '#EFEDFB', color: THEME.purple, fontWeight: 700, mb: 2.5 }}
              />

              <TextField
                fullWidth
                multiline
                minRows={3}
                size="small"
                label="Reason (optional)"
                placeholder="Share a brief note for your manager..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                sx={{ mb: 2.5 }}
              />

              <Button
                fullWidth
                size="large"
                variant="contained"
                disabled={submitting || !fromDate || !toDate || fromDate > toDate}
                onClick={handleSubmit}
                startIcon={<SendIcon />}
                sx={{
                  bgcolor: THEME.purple,
                  borderRadius: '12px',
                  py: 1.3,
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: 15,
                  boxShadow: `0 8px 18px -6px ${THEME.purple}99`,
                  '&:hover': { bgcolor: THEME.purpleDark },
                }}
              >
                {submitting ? 'Submitting...' : 'Submit Request'}
              </Button>
            </Card>
          </Grid>

          {/* RIGHT: Leave Log */}
          <Grid item xs={12} md={7}>
            <Card elevation={0} sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: '18px', border: `1px solid ${THEME.border}`, height: '100%' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5} mb={2}>
                <Stack direction="row" alignItems="center" spacing={1.2}>
                  <Avatar sx={{ bgcolor: THEME.tealBg, color: THEME.teal, width: 34, height: 34 }}>
                    <DescriptionOutlinedIcon fontSize="small" />
                  </Avatar>
                  <Typography variant="h6" fontWeight={800}>
                    Leave Log
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {['All', 'Pending', 'Approved', 'Rejected'].map((s) => {
                    const selected = filter === s;
                    return (
                      <Chip
                        key={s}
                        size="small"
                        label={`${s}${counts[s] ? ` (${counts[s]})` : ''}`}
                        onClick={() => setFilter(s)}
                        sx={{
                          fontWeight: 700,
                          bgcolor: selected ? THEME.purple : '#F5F5FA',
                          color: selected ? '#fff' : THEME.ink,
                          '&:hover': { bgcolor: selected ? THEME.purpleDark : '#ECECF7' },
                        }}
                      />
                    );
                  })}
                </Stack>
              </Stack>

              <Divider sx={{ mb: 1.5 }} />

              {loading ? (
                <ShowLoader />
              ) : filteredLeaves.length === 0 ? (
                <Box textAlign="center" py={6}>
                  <CalendarMonthIcon sx={{ fontSize: 44, color: THEME.textSecondary, mb: 1 }} />
                  <Typography fontWeight={700}>No leave requests</Typography>
                  <Typography variant="body2" sx={{ color: THEME.textSecondary }}>
                    {filter === 'All' ? "You haven't submitted any leave requests yet." : `No ${filter} leave requests found.`}
                  </Typography>
                </Box>
              ) : isMobile ? (
                // ── Mobile: stacked cards instead of a wide table ──────
                <Stack spacing={1.5} sx={{ maxHeight: 520, overflowY: 'auto', pr: 0.5 }}>
                  {filteredLeaves.map((item, idx) => {
                    // const typeConfig = LEAVE_TYPES.find((t) => t.type === item.leaveType) || LEAVE_TYPES[0];
                    const statusMeta = STATUS_META[item.status] || STATUS_META.Pending;
                    return (
                      <Box key={item._id || idx} sx={{ border: `1px solid ${THEME.border}`, borderRadius: '14px', p: 1.6 }}>
                        {/* <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                          <Chip
                            size="small"
                            icon={React.cloneElement(typeConfig.icon, { sx: { color: `${typeConfig.color} !important`, fontSize: 14 } })}
                            label={item.leaveType}
                            sx={{ bgcolor: typeConfig.bg, color: typeConfig.color, fontWeight: 700 }}
                          />
                          <Chip size="small" icon={statusMeta.icon} label={item.status} sx={{ bgcolor: statusMeta.bg, color: statusMeta.color, fontWeight: 700 }} />
                        </Stack> */}
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.8 }}>
                          <Typography variant="body2">
                            <b>{formatDisplay(item.fromDate)}</b> → <b>{formatDisplay(item.toDate)}</b>
                          </Typography>
                          <Typography variant="caption" sx={{ color: THEME.textSecondary }}>
                            {item.leaveDayType || 'Full Day'}
                          </Typography>
                        </Stack>
                        {item.reason && (
                          <Typography variant="body2" sx={{ color: THEME.textSecondary, mb: 0.8 }}>
                            {item.reason}
                          </Typography>
                        )}
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" sx={{ color: THEME.textSecondary }}>
                            Applied {formatDisplay(item.appliedOn)}
                          </Typography>
                          {/* {item.status === 'Pending' && (
                            <IconButton size="small" sx={{ color: THEME.orange }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )} */}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                // ── Desktop / tablet: table ─────────────────────────────
                <Box sx={{ overflowX: 'auto', maxHeight: 520 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>From</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>To</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Applied On</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredLeaves.map((item, idx) => {
                        // const typeConfig = LEAVE_TYPES.find((t) => t.type === item.leaveType) || LEAVE_TYPES[0];
                        const statusMeta = STATUS_META[item.status] || STATUS_META.Pending;
                        return (
                          <TableRow key={item._id || idx} hover>
                            {/* <TableCell>
                              <Chip
                                size="small"
                                icon={React.cloneElement(typeConfig.icon, { sx: { color: `${typeConfig.color} !important`, fontSize: 15 } })}
                                label={item.leaveType}
                                sx={{ bgcolor: typeConfig.bg, color: typeConfig.color, fontWeight: 700 }}
                              />
                            </TableCell> */}
                            <TableCell>{formatDisplay(item.fromDate)}</TableCell>
                            <TableCell>{formatDisplay(item.toDate)}</TableCell>
                            <TableCell>{item.leaveDayType || 'Full Day'}</TableCell>
                            <TableCell sx={{ maxWidth: 180 }}>
                              <Typography variant="body2" noWrap title={item.reason}>
                                {item.reason || '--'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip size="small" icon={statusMeta.icon} label={item.status} sx={{ bgcolor: statusMeta.bg, color: statusMeta.color, fontWeight: 700 }} />
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography variant="body2" sx={{ color: THEME.textSecondary }}>
                                  {formatDisplay(item.appliedOn)}
                                </Typography>
                                {item.status === 'Pending' && (
                                  <Tooltip title="Cancel request">
                                    <IconButton size="small" sx={{ color: THEME.orange }}>
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>
      </Box>
    </PageContainer>
  );
};

export default Leave;